/**
 * Polls webhook endpoints defined in triggerRules for event slots and evaluates
 * their conditions. Returns a stable function `isTriggerSatisfied(slotId)`.
 *
 * Each slot with triggerRules gets its own polling interval. The interval is
 * cleared and restarted whenever the slot list changes.
 */
import { useCallback, useEffect, useRef } from 'react'
import type { CompiledSlotDTO, TriggerCondition } from '@/types/player.types'

function evaluate(data: unknown, condition: TriggerCondition): boolean {
  const obj = data as Record<string, unknown>
  const raw = obj[condition.field]

  if (raw === undefined || raw === null) return false

  const actual = raw as number | string | boolean
  const expected = condition.value

  switch (condition.operator) {
    case 'eq':
      return actual === expected
    case 'ne':
      return actual !== expected
    case 'gt':
      return (actual as number) > (expected as number)
    case 'gte':
      return (actual as number) >= (expected as number)
    case 'lt':
      return (actual as number) < (expected as number)
    case 'lte':
      return (actual as number) <= (expected as number)
    case 'contains':
      return String(actual).includes(String(expected))
    default:
      return false
  }
}

export function useTriggerWatcher(slots: CompiledSlotDTO[]) {
  // slotId → boolean (whether trigger condition is currently satisfied)
  const resultsRef = useRef<Map<string, boolean>>(new Map())

  const isTriggerSatisfied = useCallback((slotId: string): boolean => {
    return resultsRef.current.get(slotId) ?? false
  }, [])

  useEffect(() => {
    const eventSlots = slots.filter((s) => s.slotType === 'event' && s.triggerRules)
    if (eventSlots.length === 0) return

    const timers: ReturnType<typeof setInterval>[] = []

    for (const slot of eventSlots) {
      const rules = slot.triggerRules
      if (!rules) continue
      const { endpoint, intervalSeconds, condition } = rules

      const poll = async () => {
        try {
          const controller = new AbortController()
          const timeout = setTimeout(() => controller.abort(), 5000)
          const res = await fetch(endpoint, { signal: controller.signal })
          clearTimeout(timeout)
          if (!res.ok) {
            resultsRef.current.set(slot.slotId, false)
            return
          }
          const data = await res.json()
          resultsRef.current.set(slot.slotId, evaluate(data, condition))
        } catch {
          resultsRef.current.set(slot.slotId, false)
        }
      }

      // immediate first poll
      void poll()
      timers.push(setInterval(poll, intervalSeconds * 1000))
    }

    return () => {
      for (const t of timers) clearInterval(t)
    }
  }, [slots])

  return { isTriggerSatisfied }
}
